import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Filter, Plus, Search } from 'lucide-react';
import { Alert } from '../../../shared/components/Alert';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { Button } from '../../../shared/components/Button';
import { EmptyState } from '../../../shared/components/EmptyState';
import { Skeleton } from '../../../shared/components/Skeleton';
import { TextInput } from '../../../shared/components/TextInput';
import { FEATURES, usePermissions } from '../../../shared/hooks/usePermissions';
import type {
  ListPropostasParams,
  OrigemProposta,
  StatusProposta,
} from '../api';
import { usePropostas } from '../hooks';
import { ORIGEM_LABEL, STATUS_LABEL, formatCurrency, formatValidade } from '../utils/format';

const PAGE_SIZE = 25;

const STATUS_OPTIONS = (Object.keys(STATUS_LABEL) as StatusProposta[]).map((s) => ({
  value: s,
  label: STATUS_LABEL[s],
}));
const ORIGEM_OPTIONS = (Object.keys(ORIGEM_LABEL) as OrigemProposta[]).map((o) => ({
  value: o,
  label: ORIGEM_LABEL[o],
}));

/**
 * Cores das badges de status (Tailwind classes). Cores foram escolhidas
 * para preservar leitura em fundo branco no modo claro — a paleta segue
 * a mesma lógica de `SituacaoBadge` da Base Comercial.
 */
const STATUS_BADGE: Record<StatusProposta, string> = {
  em_elaboracao: 'bg-gray-100 text-gray-800',
  em_revisao: 'bg-yellow-100 text-yellow-800',
  em_aprovacao: 'bg-blue-100 text-blue-800',
  aguardando_complementacao: 'bg-yellow-100 text-yellow-800',
  aprovada_internamente: 'bg-emerald-100 text-emerald-800',
  enviada_ao_cliente: 'bg-indigo-100 text-indigo-800',
  em_negociacao: 'bg-indigo-100 text-indigo-800',
  aceita: 'bg-emerald-200 text-emerald-900',
  rejeitada: 'bg-red-100 text-red-800',
  cancelada: 'bg-gray-200 text-gray-700',
  expirada: 'bg-orange-100 text-orange-800',
  convertida_em_contrato: 'bg-emerald-300 text-emerald-900',
  substituida: 'bg-gray-200 text-gray-700 line-through',
};

function StatusBadge({ status }: { status: StatusProposta }) {
  return (
    <span
      className={`inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_BADGE[status]}`}
    >
      {STATUS_LABEL[status]}
    </span>
  );
}

/**
 * Listagem de Propostas Comerciais (US-COM-006, task 15.1).
 *
 * **Padrão:** uma linha por proposta com a `currentVersion` denormalizada
 * — o backend faz o join. Auditoria de versões antigas fica na aba
 * "Versões" do detalhe (task 15.5).
 */
export default function PropostasComerciaisPage(): React.ReactElement {
  const navigate = useNavigate();
  const { can } = usePermissions();

  const [busca, setBusca] = useState('');
  const [status, setStatus] = useState<string>('');
  const [origem, setOrigem] = useState<string>('');
  const [page, setPage] = useState(0);

  const params: ListPropostasParams = useMemo(
    () => ({
      status: (status || undefined) as StatusProposta | undefined,
      origem: (origem || undefined) as OrigemProposta | undefined,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [status, origem, page],
  );

  const { data, isLoading, isError, error } = usePropostas(params);

  // Busca client-side sobre a página corrente — mesmo racional da
  // BasesComerciaisPage. O rótulo diz "nesta página".
  const linhas = useMemo(() => {
    const base = data?.data ?? [];
    if (!busca.trim()) return base;
    const termo = busca.trim().toLowerCase();
    return base.filter(
      (p) =>
        p.codigo.toLowerCase().includes(termo) ||
        (p.currentVersion?.escopo ?? '').toLowerCase().includes(termo),
    );
  }, [data, busca]);

  const totalPaginas = Math.max(1, Math.ceil((data?.total ?? 0) / PAGE_SIZE));

  return (
    <div className="p-4 sm:p-6">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Propostas Comerciais</h1>
          <p className="mt-1 text-sm text-gray-600">
            Ofertas formais versionadas por cliente — herda condições da Base Comercial.
          </p>
        </div>
        <div className="flex gap-2">
          {can(FEATURES.propostaAprovar, 'edit') && (
            <Button variant="secondary" onClick={() => navigate('/propostas/aprovacoes')}>
              <CheckCircle2 className="mr-2 h-4 w-4" />
              Aprovações
            </Button>
          )}
          {can(FEATURES.proposta, 'create') && (
            <Button onClick={() => navigate('/propostas/nova')}>
              <Plus className="mr-2 h-4 w-4" />
              Nova Proposta
            </Button>
          )}
        </div>
      </div>

      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <TextInput
          label="Buscar nesta página"
          icon={<Search className="h-4 w-4" />}
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
        <AutocompleteSelect
          label="Situação"
          icon={<Filter className="h-4 w-4" />}
          options={[{ value: '', label: 'Todas' }, ...STATUS_OPTIONS]}
          value={status}
          onChange={(v) => {
            setStatus(v);
            setPage(0);
          }}
        />
        <AutocompleteSelect
          label="Origem"
          options={[{ value: '', label: 'Todas' }, ...ORIGEM_OPTIONS]}
          value={origem}
          onChange={(v) => {
            setOrigem(v);
            setPage(0);
          }}
        />
      </div>

      {isError && (
        <Alert variant="error">
          Erro ao carregar propostas: {(error as Error)?.message ?? 'Falha desconhecida.'}
        </Alert>
      )}

      {isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
      )}

      {!isLoading && !isError && linhas.length === 0 && (
        <EmptyState
          title="Nenhuma proposta encontrada"
          description="Ajuste os filtros ou cadastre a primeira proposta para este cliente."
        />
      )}

      {!isLoading && linhas.length > 0 && (
        <>
          <div className="-mx-4 overflow-x-auto sm:mx-0">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-gray-700">Código</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-700">Versão</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-700">Origem</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-700">Validade</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-700">Valor</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-700">Situação</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {linhas.map((p) => (
                  <tr
                    key={p.id}
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => navigate(`/propostas/${p.id}`)}
                  >
                    <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-gray-600">
                      {p.codigo}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-gray-700">
                      {p.currentVersion?.versionNumber ?? '—'}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-gray-700">
                      {ORIGEM_LABEL[p.origem] ?? p.origem}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-gray-700">
                      {formatValidade(p.currentVersion?.dataValidade)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-gray-900">
                      {formatCurrency(p.currentVersion?.valorTotal)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      {p.currentVersion?.status ? (
                        <StatusBadge status={p.currentVersion.status} />
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
            <span>
              {data?.total ?? 0} registro(s) · página {page + 1} de {totalPaginas}
            </span>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                Anterior
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={page + 1 >= totalPaginas}
                onClick={() => setPage((p) => p + 1)}
              >
                Próxima
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export { StatusBadge };
